import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowRight, Loader2, Check, Zap } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerLogin() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/customer/auth/request-link`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send the link. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-amber-50 flex flex-col" data-testid="customer-login-page">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-zinc-950 rounded-lg flex items-center justify-center"><Zap size={16} className="text-white" /></div>
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Customer dashboard</span>
            </div>
            <h1 className="text-3xl font-extrabold text-zinc-950 leading-tight">Sign in to your projects</h1>
            <p className="text-sm text-zinc-600 mt-2">No passwords. We'll email you a secure sign-in link.</p>

            {sent ? (
              <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center" data-testid="magic-link-sent">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check size={20} className="text-emerald-600" />
                </div>
                <div className="font-bold text-zinc-950">Check your inbox</div>
                <p className="text-sm text-zinc-700 mt-1">We sent a sign-in link to <strong>{email}</strong>. It expires in 15 minutes.</p>
                <button onClick={() => { setSent(false); setEmail(""); }} className="mt-4 text-xs font-semibold text-zinc-700 hover:underline">Use a different email</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-3" data-testid="magic-link-form">
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input data-testid="input-customer-email" type="email" required autoFocus placeholder="you@company.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                </div>
                {error && <div data-testid="magic-link-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={!email || loading} data-testid="btn-send-magic-link"
                  className="w-full py-3 bg-zinc-950 text-white text-sm font-semibold rounded-2xl hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <>Send sign-in link <ArrowRight size={14} /></>}
                </button>
                <p className="text-[11px] text-zinc-500 text-center mt-3">
                  By signing in, you agree to our <Link to="/legal" className="underline">Terms & Privacy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
